import { motion } from 'framer-motion';
import { ChevronRight, CheckCircle2, Lock } from 'lucide-react';
import { useState } from 'react';
import { soccerPositionPaths, type PositionPath } from '@/data/soccerPositionPaths';
import { basketballPositionPaths } from '@/data/basketballPositionPaths';
import { baseballPositionPaths } from '@/data/baseballPositionPaths';
import { findSoccerLesson } from '@/data/soccerLessons';
import { findBasketballLesson } from '@/data/basketballLessons';
import { findBaseballLesson } from '@/data/baseballLessons';

interface PositionPathsProps {
  completedLessons: string[];
  totalXp: number;
  onSelectLesson: (id: string) => void;
  sport?: string;
}

export function PositionPaths({ completedLessons, totalXp, onSelectLesson, sport = 'soccer' }: PositionPathsProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const positionPaths =
    sport === 'basketball' ? basketballPositionPaths
    : sport === 'baseball' ? baseballPositionPaths
    : soccerPositionPaths;
  const findLesson =
    sport === 'basketball' ? findBasketballLesson
    : sport === 'baseball' ? findBaseballLesson
    : findSoccerLesson;

  const venueLabel =
    sport === 'soccer' ? ' on the pitch'
    : sport === 'baseball' ? ' on the diamond'
    : ' on the court';

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <h3 className="font-display font-extrabold text-lg">Position Paths</h3>
        <p className="text-xs text-muted-foreground font-body">
          Curated lesson playlists tailored to your position{venueLabel}.
        </p>
      </div>

      <div className="space-y-2">
        {positionPaths.map((pos, i) => {
          const completed = pos.lessonIds.filter(id => completedLessons.includes(id)).length;
          const total = pos.lessonIds.length;
          const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
          const isExpanded = expandedId === pos.id;

          return (
            <motion.div
              key={pos.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
            >
              <button
                onClick={() => setExpandedId(isExpanded ? null : pos.id)}
                className="w-full bg-card rounded-xl border-2 border-border p-4 shadow-card hover:shadow-elevated hover:border-primary/30 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl flex-shrink-0">{pos.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-sm">{pos.position}</p>
                    <p className="text-xs text-muted-foreground truncate">{pos.shortDescription}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {pct === 100 ? (
                      <span className="text-xs font-display font-bold text-success bg-success/10 px-2 py-0.5 rounded-full">Done ✓</span>
                    ) : (
                      <span className="text-xs font-display font-bold text-muted-foreground">{completed}/{total}</span>
                    )}
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-2.5 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </button>

              {/* Expanded lesson list */}
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-card/50 border-2 border-t-0 border-border rounded-b-xl -mt-2 pt-4 pb-2 px-4 space-y-1"
                >
                  <p className="text-xs text-muted-foreground font-body mb-3">{pos.description}</p>
                  {pos.lessonIds.map((lessonId, li) => {
                    const lessonData = findLesson(lessonId);
                    if (!lessonData) return null;
                    const isDone = completedLessons.includes(lessonId);
                    const relevance = pos.lessonRelevance[lessonId];
                    const isLocked = totalXp < lessonData.unit.requiredXp;

                    return (
                      <button
                        key={lessonId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isLocked) onSelectLesson(lessonId);
                        }}
                        disabled={isLocked}
                        className={`w-full text-left p-3 rounded-lg transition-all flex items-start gap-3 ${
                          isLocked
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-muted/50 active:scale-[0.99]'
                        }`}
                      >
                        <span className="mt-0.5 flex-shrink-0">
                          {isLocked ? (
                            <Lock className="w-4 h-4 text-muted-foreground" />
                          ) : isDone ? (
                            <CheckCircle2 className="w-4 h-4 text-success" />
                          ) : (
                            <span className="w-4 h-4 rounded-full border-2 border-muted-foreground/30 inline-block" />
                          )}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-display font-bold ${isDone ? 'text-success' : ''}`}>
                            {li + 1}. {lessonData.lesson.title}
                          </p>
                          {relevance && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{relevance}</p>
                          )}
                        </div>
                        <span className="text-xs text-accent font-display font-bold flex-shrink-0 mt-0.5">
                          +{lessonData.lesson.xpReward} XP
                        </span>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
